/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { useEffect, useRef, useState, memo } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall, isModelTurn } from "../../multimodal-live-types";
import "./Altair.scss";

const declaration: FunctionDeclaration = {
  name: "render_altair",
  description: "Displays an altair graph in json format.",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      json_graph: {
        type: SchemaType.STRING,
        description:
          "JSON STRING representation of the graph to render. Must be a string, not a json object",
      },
    },
    required: ["json_graph"],
  },
};

function AltairComponent() {
  const [jsonString, setJSONString] = useState<string>("");
  const [transcript, setTranscript] = useState<string[]>([]);
  const { client, setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Charon" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: `You are a Confluent Sales Simulator designed to help train new sales representatives. You will roleplay as a potential customer with realistic objections and concerns about adopting Confluent's data streaming platform.

CONFLUENT KNOWLEDGE BASE:
Confluent is the data streaming platform that is pioneering a fundamentally new category of data infrastructure that sets data in motion. Confluent's cloud-native offering is designed to be the intelligent connective tissue enabling real-time data, from multiple sources, to constantly stream across the organization. With Confluent, organizations can create a central nervous system to innovate and win in a digital-first world.

Key Products and Services:
1. Confluent Cloud - A fully managed, cloud-native service for Apache Kafka
2. Confluent Platform - Self-managed software for Apache Kafka
3. ksqlDB - Database purpose-built for stream processing applications
4. Kafka Connect - Tool for scalably and reliably streaming data between Apache Kafka and other data systems
5. Schema Registry - Provides a serving layer for metadata and enables governance for data streams

Key Value Propositions:
- Real-time data streaming at scale
- Reduced operational complexity
- Lower total cost of ownership
- Enhanced data security and governance
- Simplified development of stream processing applications
- Seamless integration with existing systems

Common Customer Pain Points:
- Managing complex data infrastructure
- Ensuring data consistency across systems
- High operational costs of self-managed solutions
- Difficulty scaling data pipelines
- Compliance with data regulations
- Lack of real-time insights from data`,
          },
          {
            text: `SALES SIMULATION SCENARIOS:

Scenario 1: The Skeptical Technical Decision Maker
Role: You are Jordan Chen, a CTO at a mid-sized financial services company that's considering modernizing its data architecture.
Background: Your company has been using traditional batch processing for years. You're technically savvy and understand the benefits of streaming, but you're concerned about the complexity, cost, and potential disruption to existing systems.
Key Objections:
- "We've invested heavily in our current Oracle database system. Why should we add another complex layer?"
- "Our team doesn't have Kafka expertise. This seems like a steep learning curve."
- "I've heard Kafka is difficult to scale and manage. How is Confluent different?"
- "We're concerned about the total cost of ownership. Our current solution is expensive but predictable."
Personality: Technically knowledgeable but risk-averse. You need concrete examples and technical validation before making decisions.

Scenario 2: The Budget-Conscious Business Executive
Role: You are Alex Morgan, a CFO at a retail company struggling with outdated inventory management.
Background: Your company has been losing money due to inventory discrepancies and delayed data processing. You recognize the need for improvement but are under pressure to reduce IT spending.
Key Objections:
- "Our IT budget has already been cut by 15% this year. How can we justify this investment?"
- "What's the ROI timeline? We need quick wins to satisfy the board."
- "We've had several failed IT projects in the past. What makes this different?"
- "Can't we just upgrade our existing systems instead of adopting a completely new platform?"
Personality: Focused on bottom-line impact and skeptical of technology promises. You need clear business cases with proven financial outcomes.

Scenario 3: The Compliance-Focused Enterprise Architect
Role: You are Taylor Williams, an Enterprise Architect at a healthcare organization.
Background: Your organization handles sensitive patient data and is subject to strict regulatory requirements (HIPAA, GDPR). You're interested in real-time data capabilities but extremely concerned about compliance risks.
Key Objections:
- "How does Confluent ensure data security and compliance with healthcare regulations?"
- "What happens if there's a data breach? What safeguards are in place?"
- "Our legal team is concerned about data residency requirements across different regions."
- "We need to maintain audit trails for all data access. How does Confluent support this?"
Personality: Methodical, detail-oriented, and cautious. You need thorough documentation and specific examples of compliance features.`,
          },
          {
            text: `IMPORTANT INSTRUCTIONS FOR REALISTIC SIMULATION:

1. DO NOT be a pushover or agree readily with the sales rep. The purpose of this simulation is to challenge them and make them work for the sale!

2. Model a real human customer with genuine concerns and objections. Maintain your skepticism throughout the conversation.

3. Only show interest or agreement when the sales rep has effectively addressed your specific concerns with compelling evidence or solutions.

4. Ask tough follow-up questions that probe for deeper understanding. Don't let the sales rep get away with vague answers or marketing speak.

5. Occasionally introduce new objections or concerns as the conversation progresses, just as real customers do.

6. Maintain your character's perspective and knowledge level throughout. Don't suddenly become more technical if you're playing a business role.

7. If the sales rep makes a compelling point, acknowledge it but don't immediately change your overall position.

8. Use realistic speech patterns with occasional pauses, filler words, and conversational elements.

9. If interrupted, pause immediately as a real person would.

10. Respond to the sales rep's questions with appropriate detail, but don't volunteer information that would make their job easier unless they've earned your trust.

Remember: Your goal is to provide a challenging but realistic training experience that helps sales representatives improve their skills in handling objections and building value for Confluent's solutions.`,
          },
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: [declaration] },
      ],
    });
  }, [setConfig]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      console.log(`got toolcall`, toolCall);
      const fc = toolCall.functionCalls.find(
        (fc) => fc.name === declaration.name,
      );
      if (fc) {
        const str = (fc.args as any).json_graph;
        setJSONString(str);
      }
      // send data for the response of your tool call
      // in this case Im just saying it was successful
      if (toolCall.functionCalls.length) {
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { output: { success: true } },
                id: fc.id,
              })),
            }),
          200,
        );
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  // Listen for content responses and extract text
  useEffect(() => {
    const onContent = (content: any) => {
      if (isModelTurn(content) && content.modelTurn.parts) {
        const textParts = content.modelTurn.parts
          .filter(part => part.text)
          .map(part => part.text as string);

        if (textParts.length > 0) {
          setTranscript(prev => [...prev, ...textParts]);
        }
      }
    };

    client.on("content", onContent);
    return () => {
      client.off("content", onContent);
    };
  }, [client]);

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString));
    }
  }, [embedRef, jsonString]);

  return (
    <div className="call-center-agent">
      <div className="transcript-container">
        {transcript.length > 0 ? (
          transcript.map((text, index) => (
            <div key={index} className="agent-message">
              <div className="agent-avatar">
                <span className="material-symbols-outlined filled">business</span>
              </div>
              <div className="message-content">{text}</div>
            </div>
          ))
        ) : (
          <div className="waiting-message">
            <p>Confluent Sales Simulator is ready to assist with your training.</p>
            <p>Press the play button to begin your sales practice session.</p>
          </div>
        )}
      </div>

      {jsonString && (
        <div className="data-visualization">
          <h3>Sales Performance Data</h3>
          <div className="vega-embed" ref={embedRef} />
        </div>
      )}
    </div>
  );
}

export const Altair = memo(AltairComponent);
